import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ServiceSelection, SongData } from '../../../types/planningTypes';
import { SongSearchInput } from '../SongSearchInput/SongSearchInput';
import { PlacementSearchInput } from '../PlacementSearchInput/PlacementSearchInput';
import { PerformanceTypeSearchInput } from '../PerformanceTypeSearchInput/PerformanceTypeSearchInput';
import { SelectionMetadata } from '../SelectionMetadata/SelectionMetadata';
import { Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { SongReminderBadge } from '../../../SongReminders';

interface SelectionItemProps {
  selection: ServiceSelection;
  index: number;
  total: number;
  moveSelection: (dragIndex: number, hoverIndex: number) => void;
  onDelete: () => void;
  onChange: (field: keyof ServiceSelection, value: any) => void;
  handleInputChange: (index: number, field: keyof ServiceSelection, value: string) => void;
  handleSongSelect: (index: number, songData: SongData) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const SelectionItem: React.FC<SelectionItemProps> = ({
  selection,
  index,
  total,
  moveSelection,
  onDelete,
  onChange,
  handleInputChange,
  handleSongSelect,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: string | null }>({
    accept: 'selection',
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId() as string | null,
    }),
    hover(item: DragItem, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveSelection(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'selection',
    item: () => ({ id: selection.service_selection_id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Only attach drag handle to the handle element
  drag(dragHandleRef);
  // Attach drop target to the whole component
  drop(ref);

  return (
    <div
      ref={ref}
      className={`card mb-2 ${isDragging ? 'opacity-75' : ''}`}
      data-handler-id={handlerId}
    >
      <div className="card-body py-2">
        <div className="d-flex align-items-start">
          {/* Drag Handle (desktop) + Up/Down arrows (works everywhere, esp. mobile) */}
          <div className="d-flex flex-column align-items-center me-2 me-md-3 user-select-none">
            <button
              type="button"
              className="btn btn-sm btn-link text-secondary p-0"
              style={{ lineHeight: 1, minWidth: 32, minHeight: 32 }}
              onClick={() => moveSelection(index, index - 1)}
              disabled={index === 0}
              title="Move up"
              aria-label="Move up"
            >
              <ChevronUp size={20} />
            </button>
            <div
              ref={dragHandleRef}
              className="text-muted d-none d-md-block"
              style={{ cursor: 'grab', touchAction: 'none', fontSize: '1.1rem', lineHeight: 1 }}
              title="Drag to reorder"
            >
              ⋮⋮
            </div>
            <button
              type="button"
              className="btn btn-sm btn-link text-secondary p-0"
              style={{ lineHeight: 1, minWidth: 32, minHeight: 32 }}
              onClick={() => moveSelection(index, index + 1)}
              disabled={index >= total - 1}
              title="Move down"
              aria-label="Move down"
            >
              <ChevronDown size={20} />
            </button>
          </div>
          
          {/* Main Content - Now selectable */}
          <div className="flex-grow-1" style={{ userSelect: 'text' }}>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="mb-2">
                  <PlacementSearchInput
                    value={selection.placement || ''}
                    onChange={(value) => onChange('placement', value)}
                  />
                </div>
                <div className="mb-2">
                  <PerformanceTypeSearchInput
                    value={selection.performance_type || ''}
                    onChange={(value) => onChange('performance_type', value)}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Director notes (e.g., oboe needed)"
                    value={selection.director_notes || ''}
                    onChange={(e) => onChange('director_notes', e.target.value)}
                    style={{ fontSize: '12px' }}
                  />
                </div>
              </div>
              <div className="col-md-8">
                <div>
                  <SongSearchInput
                    onSelect={(songData) => handleSongSelect(index, songData)}
                    defaultValue={selection.song_title}
                  />
                  <SelectionMetadata selection={selection} />
                  <SongReminderBadge songId={selection.song_id} title={selection.song_title} />
                </div>
              </div>
            </div>
          </div>

          {/* Delete Button */}
          <button
            className="btn btn-link text-danger p-0 ms-3"
            onClick={onDelete}
            title="Delete selection"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectionItem;
